/**
 * Formats a date string to "YYYY-MM-DD" for HTML date input elements.
 * @param dateString The date string to format (e.g., ISO 8601, "M/D/YY", "MM/DD/YYYY").
 * @returns The formatted date string "YYYY-MM-DD", or an empty string if input is invalid or null/undefined.
 */
export const formatDateForInput = (dateString: string | undefined | null): string => {
    if (!dateString) {
        return '';
    }
    // new Date() can parse various formats, including ISO 8601 strings
    // (e.g., "2023-03-15T00:00:00.000Z") and simpler date strings.
    const date = new Date(dateString);
    // Check if the date is valid
    if (isNaN(date.getTime())) {
        console.warn(`Invalid date string received for formatting: ${dateString}`);
        return ''; // Return empty for invalid dates to avoid input errors
    }
    
    // Use UTC methods to avoid timezone conversion issues
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0'); // getUTCMonth() is 0-indexed
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Formats a date string to "MM/DD/YYYY" for display purposes.
 * @param dateString The date string to format (e.g., ISO 8601, "M/D/YY", "MM/DD/YYYY").
 * @returns The formatted date string "MM/DD/YYYY", or an empty string if input is invalid or null/undefined.
 */
export const formatDateForDisplay = (dateString: string | null): string => {
    if (!dateString) {
        return '';
    }
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
        console.warn(`Invalid date string received for display formatting: ${dateString}`);
        return '';
    }
    
    // Use UTC methods to avoid timezone conversion issues
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    
    return `${month}/${day}/${year}`;
}

/**
 * Formats a Date object to "MM/DD/YYYY" for display purposes.
 * This function is timezone-safe and uses UTC methods.
 * @param date The Date object to format.
 * @returns The formatted date string "MM/DD/YYYY", or an empty string if input is invalid or null/undefined.
 */
export const formatDateObjectForDisplay = (date: Date | null | undefined): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return '';
    }
    
    // Use UTC methods to avoid timezone conversion issues
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    
    return `${month}/${day}/${year}`;
}